import { useEffect, useState } from "react";
import {
  StreamSignalStateType,
  StreamStatusType,
} from "@/types/graphql/partner.generated";

/**
 * Сигнал эфира (Stream.signal): есть ли видео по live HLS, проверка раз в
 * минуту на бэкенде (спека 2026-10-24-stream-monitoring-ux, часть 1).
 * Смысл у сигнала есть только у идущего эфира: после FINISHED в документе
 * остаётся последнее состояние, его не показываем.
 */
export interface StreamSignalLike {
  state: StreamSignalStateType;
  since: string;
}

export type StreamSignalProblem = "NO_VIDEO" | "DISCONNECTED";

/** Сигнал, который стоит показать: только у STARTED. */
export function getActiveStreamSignal(stream: {
  status: StreamStatusType;
  signal?: StreamSignalLike | null;
}): StreamSignalLike | null {
  if (stream.status !== StreamStatusType.StartedType) return null;
  return stream.signal ?? null;
}

/** Проблема с видео у идущего эфира или null, если видео идёт. */
export function getStreamSignalProblem(stream: {
  status: StreamStatusType;
  signal?: StreamSignalLike | null;
}): StreamSignalProblem | null {
  const signal = getActiveStreamSignal(stream);
  if (!signal) return null;
  if (signal.state === StreamSignalStateType.NoVideoType) return "NO_VIDEO";
  if (signal.state === StreamSignalStateType.DisconnectedType) {
    return "DISCONNECTED";
  }
  return null;
}

/** Целые минуты с момента `since`, не меньше нуля. */
export function minutesSince(since: string, now: Date): number {
  const ms = now.getTime() - new Date(since).getTime();
  return ms > 0 ? Math.floor(ms / 60_000) : 0;
}

/**
 * Текущее время с тиком раз в `intervalMs`, пока `enabled`: метка «Нет
 * видео N мин» растёт сама, без перезапроса списка.
 */
export function useNow(enabled: boolean, intervalMs = 30_000): Date {
  const [now, setNow] = useState(() => new Date());
  useEffect(() => {
    if (!enabled) return undefined;
    setNow(new Date());
    const timer = window.setInterval(() => setNow(new Date()), intervalMs);
    return () => window.clearInterval(timer);
  }, [enabled, intervalMs]);
  return now;
}
